/**
 * Client-side tool support for the Voice Live adapter.
 *
 * Pure, transport-free helpers: `parseFunctionCall` recognizes a completed
 * Realtime function call in a data-channel server event, and `runClientTool`
 * executes the host-registered handler and emits the `function_call_output` +
 * `response.create` client events via a caller-supplied `send`. The provider
 * wires `send` to its data channel; handler execution lives in the adapter.
 */

import type { ClientTool } from "@microsoft/voice-widget-core-client";
import type { RealtimeServerEvent } from "./realtime-events.js";

export interface ParsedFunctionCall {
  name: string;
  callId: string;
  args: Record<string, unknown>;
}

function asRecord(v: unknown): Record<string, unknown> | undefined {
  return v && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : undefined;
}

/** Parse the arguments JSON string into an object; `{}` on empty/invalid. */
function parseArgs(raw: unknown): Record<string, unknown> {
  if (typeof raw !== "string" || raw.trim() === "") return {};
  try {
    const parsed = JSON.parse(raw) as unknown;
    return asRecord(parsed) ?? {};
  } catch {
    return {};
  }
}

/** A completed `function_call` conversation item, wherever it appears. */
function fromItem(item: Record<string, unknown> | undefined): ParsedFunctionCall | null {
  if (!item || item["type"] !== "function_call") return null;
  const name = item["name"];
  const callId = item["call_id"];
  if (typeof name !== "string" || typeof callId !== "string") return null;
  return { name, callId, args: parseArgs(item["arguments"]) };
}

/**
 * Recognize the completed function call(s) in a data-channel server event. Handles the
 * two self-contained, data-channel-delivered shapes: `response.output_item.done`
 * (one `function_call` item) and `response.done` (any number of `function_call`s in its
 * output). Returns every call found — a `response.done` can carry parallel tool calls,
 * so returning only the first would silently drop the rest. Stateless — the caller
 * dedupes by `callId`.
 */
export function parseFunctionCalls(event: RealtimeServerEvent): ParsedFunctionCall[] {
  const calls: ParsedFunctionCall[] = [];
  if (event.type === "response.output_item.done") {
    const call = fromItem(asRecord(event["item"]));
    if (call) calls.push(call);
    return calls;
  }
  if (event.type === "response.done") {
    const output = asRecord(event["response"])?.["output"];
    if (Array.isArray(output)) {
      for (const entry of output) {
        const call = fromItem(asRecord(entry));
        if (call) calls.push(call);
      }
    }
  }
  return calls;
}

/**
 * Single-call convenience wrapper over {@link parseFunctionCalls} (first call or null).
 */
export function parseFunctionCall(event: RealtimeServerEvent): ParsedFunctionCall | null {
  return parseFunctionCalls(event)[0] ?? null;
}

/**
 * Execute a host-registered client tool and return its result to the model.
 * Unknown tool / thrown handler → an error `function_call_output` (never throws).
 * A `void`/`undefined` return is acked as `{ ok: true }`.
 * Unknown tool / thrown handler always warrant a follow-up turn; success honors `followUpResponse`.
 *
 * `send` reports **delivery**: `false` means the event never reached the gateway. The
 * output and the follow-up are two separate sends, and the smaller `response.create` can
 * succeed after the larger `function_call_output` was dropped — which would ask the model
 * to speak with no tool result. So an undelivered output suppresses the follow-up.
 */
export async function runClientTool(
  call: ParsedFunctionCall,
  handler: ClientTool | undefined,
  followUpResponse: boolean,
  send: (event: unknown) => boolean,
): Promise<void> {
  let output: string;
  let followUp = true; // unknown tool / thrown handler always warrant a follow-up turn
  try {
    if (!handler) {
      output = JSON.stringify({ error: `unknown tool: ${call.name}` });
    } else {
      const result = await handler(call.args);
      output = JSON.stringify(result === undefined ? { ok: true } : result);
      followUp = followUpResponse; // success: honor the broker policy
    }
  } catch (e) {
    output = JSON.stringify({ error: e instanceof Error ? e.message : String(e) });
  }
  const delivered = send({
    type: "conversation.item.create",
    item: { type: "function_call_output", call_id: call.callId, output },
  });
  if (!delivered) return;
  if (followUp) send({ type: "response.create" });
}
